import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import {
  BlueprintProfile,
  LLMProvider,
  SkillComponentMap,
  SkillNodeMap,
  SkillTag,
  activateLLMProvider,
  fetchBlueprintProfiles,
  fetchLLMProviders,
  fetchSkillComponents,
  fetchSkillMapProblems,
  fetchSkillNodes,
  fetchSkillTags,
  rebuildBlueprintProfile,
  updateBlueprintProfile,
  updateSkillComponent,
  updateSkillNode,
  updateSkillTag,
} from './api';

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const errorText = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

/** Inline editor used across the tables: click to edit, blur/Enter to save,
 *  Escape to abandon. Saving is optimistic-free — the row only changes once
 *  the server echoes it back, so a rejected edit cannot look applied. */
const EditableCell: React.FC<{
  value: string | number;
  onSave: (next: string) => Promise<void>;
  textarea?: boolean;
  suffix?: string;
  width?: number;
}> = ({ value, onSave, textarea, suffix, width }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ''));
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(String(value ?? '')), [value]);

  const commit = async () => {
    if (draft === String(value ?? '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer', display: 'inline-block', minWidth: width ?? 40 }}
        onClick={() => setEditing(true)}
        onKeyDown={event => {
          if (event.key === 'Enter') setEditing(true);
        }}
      >
        {String(value ?? '') || <Text type="secondary">—</Text>}
        {suffix}
      </span>
    );
  }

  const shared = {
    value: draft,
    disabled: saving,
    autoFocus: true,
    onBlur: commit,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setDraft(event.target.value),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDraft(String(value ?? ''));
        setEditing(false);
      }
    },
  };

  return textarea ? (
    <Input.TextArea {...shared} autoSize={{ minRows: 2, maxRows: 8 }} />
  ) : (
    <Input {...shared} onPressEnter={commit} style={{ width: width ?? 90 }} />
  );
};

const SkillMapAdmin: React.FC = () => {
  const [tags, setTags] = useState<SkillTag[]>([]);
  const [components, setComponents] = useState<SkillComponentMap[]>([]);
  const [nodes, setNodes] = useState<SkillNodeMap[]>([]);
  const [profiles, setProfiles] = useState<BlueprintProfile[]>([]);
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [problems, setProblems] = useState<string[] | undefined>();
  const [planCode, setPlanCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const [t, c, n, p, l] = await Promise.all([
        fetchSkillTags(),
        fetchSkillComponents(),
        fetchSkillNodes(),
        fetchBlueprintProfiles(),
        fetchLLMProviders(),
      ]);
      setTags(t);
      setComponents(c);
      setNodes(n);
      setProfiles(p);
      setProviders(l);
      try {
        const report = await fetchSkillMapProblems();
        setProblems(report.problems);
        setPlanCode(report.plan);
      } catch {
        setProblems(undefined); // the check is advisory; never block the page
      }
    } catch (requestError) {
      setError(errorText(requestError, 'The skill map could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const guard = async (key: string, run: () => Promise<void>) => {
    setBusy(key);
    setError(undefined);
    try {
      await run();
    } catch (requestError) {
      setError(errorText(requestError, 'The change could not be saved.'));
    } finally {
      setBusy(undefined);
    }
  };

  const tagByCode = useMemo(
    () => Object.fromEntries(tags.map(tag => [tag.code, tag])),
    [tags],
  );

  /* ---- tags ------------------------------------------------------------- */
  const tagColumns = [
    { title: 'Code', dataIndex: 'code', width: 190, render: (v: string) => <Text code>{v}</Text> },
    { title: 'Family', dataIndex: 'family', width: 120 },
    { title: 'Name', dataIndex: 'name', width: 190 },
    {
      title: 'Definition (read by the recommender)',
      dataIndex: 'definition',
      render: (value: string, row: SkillTag) => (
        <EditableCell
          value={value}
          textarea
          onSave={next =>
            guard(`tag-${row.id}`, async () => {
              const saved = await updateSkillTag(row.id, { definition: next });
              setTags(current => current.map(t => (t.id === saved.id ? saved : t)));
            })
          }
        />
      ),
    },
    {
      title: 'Diagnosable',
      dataIndex: 'is_diagnosable',
      width: 120,
      align: 'center' as const,
      render: (value: boolean, row: SkillTag) => (
        <Tooltip title="Off = defined but never diagnosed. The diagnosis emits no weakness for it — deliberately not a zero, which would read as 'bad at this'.">
          <Switch
            checked={value}
            loading={busy === `tag-${row.id}`}
            onChange={checked =>
              guard(`tag-${row.id}`, async () => {
                const saved = await updateSkillTag(row.id, { is_diagnosable: checked });
                setTags(current => current.map(t => (t.id === saved.id ? saved : t)));
              })
            }
          />
        </Tooltip>
      ),
    },
  ];

  /* ---- components ------------------------------------------------------- */
  const componentColumns = [
    { title: 'Engine component', dataIndex: 'component', width: 230, render: (v: string) => <Text code>{v}</Text> },
    { title: 'P', dataIndex: 'pillar', width: 50, align: 'center' as const },
    {
      title: 'Skill tag',
      dataIndex: 'tag_code',
      width: 190,
      render: (value: string) => <Tag color={tagByCode[value]?.is_diagnosable ? 'blue' : 'default'}>{value}</Tag>,
    },
    { title: 'Weight', dataIndex: 'weight', width: 90, align: 'right' as const, render: (v: number) => v.toFixed(3) },
    {
      title: 'Weak below',
      dataIndex: 'weak_below',
      width: 120,
      align: 'right' as const,
      render: (value: number, row: SkillComponentMap) => (
        <EditableCell
          value={value}
          width={70}
          onSave={next =>
            guard(`comp-${row.id}`, async () => {
              const saved = await updateSkillComponent(row.id, { weak_below: Number(next) });
              setComponents(current => current.map(c => (c.id === saved.id ? saved : c)));
            })
          }
        />
      ),
    },
    {
      title: 'Strong above',
      dataIndex: 'strong_above',
      width: 120,
      align: 'right' as const,
      render: (value: number, row: SkillComponentMap) => (
        <EditableCell
          value={value}
          width={70}
          onSave={next =>
            guard(`comp-${row.id}`, async () => {
              const saved = await updateSkillComponent(row.id, { strong_above: Number(next) });
              setComponents(current => current.map(c => (c.id === saved.id ? saved : c)));
            })
          }
        />
      ),
    },
    { title: 'Note', dataIndex: 'note', render: (v: string) => <Text type="secondary">{v}</Text> },
  ];

  /* ---- nodes ------------------------------------------------------------ */
  const nodeColumns = [
    { title: 'Node', dataIndex: 'node_internal_code', width: 110, render: (v: string) => <Text code>{v}</Text> },
    {
      title: 'Trains',
      dataIndex: 'tag_code',
      width: 190,
      render: (value: string) => <Tag color={tagByCode[value]?.is_diagnosable ? 'blue' : 'default'}>{value}</Tag>,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      width: 100,
      render: (value: string) => <Tag color={value === 'primary' ? 'gold' : undefined}>{value}</Tag>,
    },
    {
      title: 'Themes',
      dataIndex: 'themes',
      width: 220,
      render: (value: string[]) =>
        value?.length ? value.map(t => <Tag key={t}>{t}</Tag>) : <Text type="secondary">any</Text>,
    },
    {
      title: 'Recommend when (the trainee MISTAKE, not the lesson)',
      dataIndex: 'indication',
      render: (value: string, row: SkillNodeMap) => (
        <EditableCell
          value={value}
          textarea
          onSave={next =>
            guard(`node-${row.id}`, async () => {
              const saved = await updateSkillNode(row.id, { indication: next });
              setNodes(current => current.map(n => (n.id === saved.id ? saved : n)));
            })
          }
        />
      ),
    },
  ];

  /* ---- blueprint profiles ----------------------------------------------- */
  const profileColumns = [
    { title: 'Blueprint', dataIndex: 'slug', width: 300, render: (v: string) => <Text code>{v}</Text> },
    { title: 'Scenario', dataIndex: 'scenario_code', width: 140 },
    {
      title: 'Instruments',
      dataIndex: 'instruments',
      width: 200,
      render: (value: string[]) => (value || []).map(i => <Tag key={i}>{i}</Tag>),
    },
    { title: 'Periods', dataIndex: 'periods', width: 80, align: 'right' as const },
    { title: 'News', dataIndex: 'news_count', width: 70, align: 'right' as const },
    {
      title: 'Peak conviction',
      dataIndex: 'max_abs_pcs',
      width: 130,
      align: 'right' as const,
      render: (v: number) => `${v?.toFixed(2)} / 5`,
    },
    {
      title: 'Summary (shown to the recommender)',
      dataIndex: 'summary',
      render: (value: string, row: BlueprintProfile) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <EditableCell
            value={value}
            textarea
            onSave={next =>
              guard(`profile-${row.id}`, async () => {
                const saved = await updateBlueprintProfile(row.slug, next);
                setProfiles(current => current.map(p => (p.id === saved.id ? saved : p)));
              })
            }
          />
          {row.overridden && <Tag color="orange">edited — regeneration will not overwrite</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      width: 110,
      render: (_v: unknown, row: BlueprintProfile) => (
        <Button
          size="small"
          loading={busy === `profile-${row.id}`}
          onClick={() =>
            guard(`profile-${row.id}`, async () => {
              const saved = await rebuildBlueprintProfile(row.slug);
              setProfiles(current => current.map(p => (p.id === saved.id ? saved : p)));
            })
          }
        >
          Rebuild
        </Button>
      ),
    },
  ];

  /* ---- providers -------------------------------------------------------- */
  const providerColumns = [
    { title: 'Name', dataIndex: 'name', width: 190 },
    { title: 'Kind', dataIndex: 'kind', width: 120, render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Model', dataIndex: 'model', width: 240 },
    { title: 'Base URL', dataIndex: 'base_url' },
    {
      title: 'Key',
      dataIndex: 'has_api_key',
      width: 70,
      align: 'center' as const,
      render: (value: boolean) => (value ? <Tag color="green">set</Tag> : <Text type="secondary">—</Text>),
    },
    { title: 'Priority', dataIndex: 'priority', width: 90, align: 'right' as const },
    {
      title: 'Primary',
      dataIndex: 'is_active',
      width: 110,
      align: 'center' as const,
      render: (value: boolean, row: LLMProvider) =>
        value ? (
          <Tag color="green">active</Tag>
        ) : (
          <Button
            size="small"
            loading={busy === `prov-${row.id}`}
            onClick={() =>
              guard(`prov-${row.id}`, async () => {
                await activateLLMProvider(row.id);
                setProviders(await fetchLLMProviders());
              })
            }
          >
            Activate
          </Button>
        ),
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 4px 40px' }}>
      <Typography.Title level={4} style={{ marginBottom: 4 }}>
        Skill Map
      </Typography.Title>
      <Paragraph type="secondary" style={{ maxWidth: 900 }}>
        The curation the recommendation system reads: which engine components
        measure each skill, which nodes teach it, and what the recommender is
        told about each scenario. Edits take effect on the next run — no deploy.
      </Paragraph>

      {error && (
        <Alert type="error" showIcon closable message={error} style={{ marginBottom: 12 }}
          onClose={() => setError(undefined)} />
      )}

      {problems !== undefined &&
        (problems.length ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${problems.length} skill-map problem(s) on ${planCode}`}
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {problems.map(problem => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            }
          />
        ) : (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Skill map is complete for ${planCode} — every scored component is mapped, every diagnosable skill is both measured and taught, and every active node is visible to the recommender.`}
          />
        ))}

      <Space style={{ marginBottom: 12 }}>
        <Button onClick={load}>Reload</Button>
      </Space>

      <Tabs defaultActiveKey="tags">
        <TabPane tab={`Skills (${tags.length})`} key="tags">
          <Card size="small">
            <Table
              rowKey="id"
              size="small"
              dataSource={tags}
              columns={tagColumns}
              pagination={false}
            />
          </Card>
        </TabPane>

        <TabPane tab={`Components (${components.length})`} key="components">
          <Card size="small">
            <Paragraph type="secondary">
              Bands decide what counts as weak. They are per component on
              purpose: production medians run from 0.13 to 1.00, so one global
              threshold cannot work.
            </Paragraph>
            <Table
              rowKey="id"
              size="small"
              dataSource={components}
              columns={componentColumns}
              pagination={false}
            />
          </Card>
        </TabPane>

        <TabPane tab={`Nodes (${nodes.length})`} key="nodes">
          <Card size="small">
            <Table
              rowKey="id"
              size="small"
              dataSource={nodes}
              columns={nodeColumns}
              pagination={{ pageSize: 25 }}
            />
          </Card>
        </TabPane>

        <TabPane tab={`Scenarios (${profiles.length})`} key="profiles">
          <Card size="small">
            <Paragraph type="secondary">
              Derived from each scenario&apos;s own content and refreshed on
              ingest. Editing a summary pins it — regeneration will leave it
              alone.
            </Paragraph>
            <Table
              rowKey="id"
              size="small"
              dataSource={profiles}
              columns={profileColumns}
              pagination={{ pageSize: 20 }}
            />
          </Card>
        </TabPane>

        <TabPane tab={`LLM providers (${providers.length})`} key="providers">
          <Card size="small">
            <Paragraph type="secondary">
              One primary; other enabled rows are failover targets in priority
              order. With none configured the recommender still runs and writes
              template rationales.
            </Paragraph>
            <Table
              rowKey="id"
              size="small"
              dataSource={providers}
              columns={providerColumns}
              pagination={false}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default SkillMapAdmin;
